import { badgeCatalog } from "./catalog.js";
import {
    decodeHashTarget,
    filterAndSortEntries,
    paginateEntries,
    parseGalleryView,
    parseSortMode,
} from "./gallery-state.js";

/**
 * @typedef {"classic" | "flat"} BadgeStyle
 */

/** @typedef {"grid" | "list"} GalleryView */
/**
 * @typedef {"badges-asc"
 *     | "badges-desc"
 *     | "category-asc"
 *     | "featured"
 *     | "title-asc"
 *     | "title-desc"} SortMode
 */

/**
 * @typedef {object} BadgeCatalogEntry
 *
 * @property {number} badgeCount
 * @property {string} category
 * @property {string} description
 * @property {string} id
 * @property {string[]} languages
 * @property {string[]} placeholders
 * @property {Record<string, number>} providerBadgeCounts
 * @property {string[]} providers
 * @property {number} sourceLine
 * @property {string} template
 * @property {string} title
 */

/**
 * @typedef {object} BadgeLink
 *
 * @property {string} alt
 * @property {string} image
 * @property {string} target
 */

/**
 * @typedef {object} AppState
 *
 * @property {string} branch
 * @property {string} category
 * @property {string} language
 * @property {string} owner
 * @property {number} page
 * @property {number} pageSize
 * @property {string} query
 * @property {string} repo
 * @property {string} service
 * @property {SortMode} sort
 * @property {BadgeStyle} style
 * @property {GalleryView} view
 */

const repositoryUrl = "https://github.com/Nick2bad4u/github-badge-layouts";
const storageKey = "github-badge-layouts:preferences:v1";
const defaultState = Object.freeze({
    branch: "main",
    category: "all",
    language: "all",
    owner: "Nick2bad4u",
    page: 1,
    pageSize: 6,
    query: "",
    repo: "gh-runs-cleanup",
    service: "all",
    sort: /** @type {SortMode} */ ("featured"),
    style: /** @type {BadgeStyle} */ ("flat"),
    view: /** @type {GalleryView} */ ("grid"),
});
const allowedPreviewImageHosts = new Set(
    badgeCatalog.providers.flatMap((provider) => [
        ...provider.imageHosts,
        ...provider.deliveryHosts,
    ])
);

/** @type {Readonly<Record<SortMode, string>>} */
const sortLabels = Object.freeze({
    "badges-asc": "badge count, low to high",
    "badges-desc": "badge count, high to low",
    "category-asc": "category, then title",
    featured: "featured order",
    "title-asc": "title, A to Z",
    "title-desc": "title, Z to A",
});

/** @type {Map<string, Record<string, string>>} */
const valuesByEntry = new Map();
/** @type {{ timer: ReturnType<typeof setTimeout> | null }} */
const toastState = { timer: null };
/** @type {{ timer: ReturnType<typeof setTimeout> | null }} */
const contextRenderState = { timer: null };

/**
 * @template {Element} T
 *
 * @param {ParentNode} root
 * @param {string} selector
 *
 * @returns {T}
 */
function queryRequired(root, selector) {
    const element = root.querySelector(selector);
    if (!element) {
        throw new Error(`Required page element is missing: ${selector}`);
    }
    return /** @type {T} */ (element);
}

/** @type {HTMLInputElement} */
const ownerInput = queryRequired(document, "#owner");
/** @type {HTMLInputElement} */
const repositoryInput = queryRequired(document, "#repository");
/** @type {HTMLInputElement} */
const branchInput = queryRequired(document, "#branch");
/** @type {HTMLInputElement} */
const searchInput = queryRequired(document, "#search");
/** @type {HTMLSelectElement} */
const categorySelect = queryRequired(document, "#category");
/** @type {HTMLSelectElement} */
const languageSelect = queryRequired(document, "#language");
/** @type {HTMLSelectElement} */
const serviceSelect = queryRequired(document, "#service");
/** @type {HTMLSelectElement} */
const sortSelect = queryRequired(document, "#sort");
/** @type {HTMLSelectElement} */
const pageSizeSelect = queryRequired(document, "#page-size");
/** @type {HTMLElement} */
const contextSummary = queryRequired(document, "#context-summary");
/** @type {HTMLElement} */
const grid = queryRequired(document, "#layout-grid");
/** @type {HTMLElement} */
const emptyState = queryRequired(document, "#empty-state");
/** @type {HTMLElement} */
const resultCount = queryRequired(document, "#result-count");
/** @type {HTMLElement} */
const previewNoteText = queryRequired(document, "#preview-note-text");
/** @type {HTMLElement} */
const pagination = queryRequired(document, "#pagination");
/** @type {HTMLElement} */
const pageList = queryRequired(document, "#page-list");
/** @type {HTMLElement} */
const pageSummary = queryRequired(document, "#page-summary");
/** @type {HTMLButtonElement} */
const firstPageButton = queryRequired(document, "#first-page");
/** @type {HTMLButtonElement} */
const previousPageButton = queryRequired(document, "#previous-page");
/** @type {HTMLButtonElement} */
const nextPageButton = queryRequired(document, "#next-page");
/** @type {HTMLButtonElement} */
const lastPageButton = queryRequired(document, "#last-page");
/** @type {HTMLButtonElement} */
const resetFiltersButton = queryRequired(document, "#reset-filters");
/** @type {HTMLButtonElement} */
const resetContextButton = queryRequired(document, "#reset-context");
/** @type {HTMLElement} */
const toast = queryRequired(document, "#toast");

/** @type {Readonly<Record<string, string>>} */
const generalExamples = Object.freeze({
    ACTION_SLUG: "checkout",
    ADDON_SLUG: "ublock-origin",
    APP_ID: "org.fdroid.fdroid",
    APPVEYOR_ACCOUNT: "gruntjs",
    APPVEYOR_PROJECT: "grunt",
    ARCH: "amd64",
    ARTIFACT_ID: "commons-lang3",
    AZURE_ORG: "dnceng",
    AZURE_PIPELINE: "51",
    AZURE_PROJECT: "public",
    CASK: "firefox",
    CI_BRANCH: "master",
    CI_OWNER: "circleci",
    CI_REPO: "circleci-docs",
    CODECLIMATE_ORG: "codeclimate",
    CODECLIMATE_REPO: "codeclimate",
    COLLECTIVE: "babel",
    CRATE: "serde",
    DISCORD_ID_OR_SLUG: "81384788765712384",
    DISTRIBUTION: "Moose",
    DOCKER_SCOPE: "library",
    EXTENSION: "vscode-yaml",
    EXTENSION_ID: "cjpalhdlnbpafiamejdnhcphjbkeiagm",
    FORMULA: "git",
    GEM: "rails",
    GITLAB_NAMESPACE: "gitlab-org",
    GROUP_ID: "org.apache.commons",
    IMAGE: "alpine",
    INVITE_CODE: "discord-developers",
    LIBERAPAY_ACCOUNT: "GIMP",
    MATRIX_ROOM: "rust",
    MATRIX_SERVER: "matrix.org",
    MODULE: "Pester",
    NAMESPACE: "redhat",
    PACKAGE: "eslint",
    POD: "Alamofire",
    "PUBLISHER.EXTENSION": "ms-vscode.cpptools",
    SCOOP_PACKAGE: "git",
    SNAP: "code",
    SNYK_ORG: "snyk-labs",
    SNYK_PROJECT_ID: "goof",
    TAG: "latest",
    TOOL_NAME: "ESLint plugin",
    UPTIME_ROBOT_MONITOR_KEY: "m780862947-3e92c0970c6c07c494b14c40",
    VENDOR: "monolog",
    WINGET_PACKAGE_ID: "Git.Git",
});

/** @type {{ test: RegExp; values: Record<string, string> }[]} */
const ecosystemExamples = [
    {
        test: /GitLab/v,
        values: { GITLAB_NAMESPACE: "gitlab-org", REPO: "gitlab" },
    },
    {
        test: /JavaScript|TypeScript|npm/v,
        values: { OWNER: "eslint", PACKAGE: "eslint", REPO: "eslint" },
    },
    { test: /Docker/v, values: { DOCKER_SCOPE: "library", IMAGE: "alpine" } },
    {
        test: /Go module/v,
        values: {
            MODULE: "github.com/gin-gonic/gin",
            OWNER: "gin-gonic",
            REPO: "gin",
        },
    },
    {
        test: /PowerShell/v,
        values: { MODULE: "Pester", OWNER: "pester", REPO: "Pester" },
    },
    {
        test: /PyPI|Python/v,
        values: { OWNER: "psf", PACKAGE: "requests", REPO: "requests" },
    },
    {
        test: /NuGet|\.NET/v,
        values: {
            OWNER: "JamesNK",
            PACKAGE: "Newtonsoft.Json",
            REPO: "Newtonsoft.Json",
        },
    },
    {
        test: /Rust/v,
        values: { CRATE: "serde", OWNER: "serde-rs", REPO: "serde" },
    },
    {
        test: /Emacs|MELPA/v,
        values: { OWNER: "magit", PACKAGE: "magit", REPO: "magit" },
    },
];

/** @returns {Partial<AppState>} */
function loadStoredPreferences() {
    const raw = localStorage.getItem(storageKey);
    if (raw === null || raw.length === 0) return {};
    try {
        return parseStoredPreferences(raw);
    } catch {
        return {};
    }
}

/** @param {string | null} value @param {number} fallback */
function parsePositiveInteger(value, fallback) {
    if (value === null) return fallback;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
}

/** @param {string} raw @returns {Partial<AppState>} */
function parseStoredPreferences(raw) {
    const parsed = /** @type {unknown} */ (JSON.parse(raw));
    if (typeof parsed !== "object" || parsed === null) return {};

    const record = /** @type {Record<string, unknown>} */ (parsed);
    /** @type {Partial<AppState>} */
    const preferences = {};
    if (typeof record["pageSize"] === "number")
        preferences.pageSize = record["pageSize"];
    if (typeof record["sort"] === "string") {
        preferences.sort = parseSortMode(record["sort"], defaultState.sort);
    }
    if (record["style"] === "classic" || record["style"] === "flat") {
        preferences.style = record["style"];
    }
    if (typeof record["view"] === "string") {
        preferences.view = parseGalleryView(record["view"], defaultState.view);
    }
    return preferences;
}

/** @param {string | null} value @param {BadgeStyle} fallback */
function parseStyle(value, fallback) {
    if (value === "classic") return "classic";
    if (value === "flat") return "flat";
    return fallback === "classic" ? "classic" : "flat";
}

const storedPreferences = loadStoredPreferences();
const initialParameters = new URLSearchParams(location.search);
/** @type {AppState} */
const state = {
    branch: initialParameters.get("branch") ?? defaultState.branch,
    category: initialParameters.get("category") ?? defaultState.category,
    language: initialParameters.get("language") ?? defaultState.language,
    owner: initialParameters.get("owner") ?? defaultState.owner,
    page: parsePositiveInteger(
        initialParameters.get("page"),
        defaultState.page
    ),
    pageSize: parsePositiveInteger(
        initialParameters.get("perPage"),
        typeof storedPreferences.pageSize === "number"
            ? storedPreferences.pageSize
            : defaultState.pageSize
    ),
    query: initialParameters.get("q") ?? defaultState.query,
    repo: initialParameters.get("repo") ?? defaultState.repo,
    service: initialParameters.get("service") ?? defaultState.service,
    sort: parseSortMode(
        initialParameters.get("sort"),
        storedPreferences.sort ?? defaultState.sort
    ),
    style: parseStyle(
        initialParameters.get("style"),
        parseStyle(
            typeof storedPreferences.style === "string"
                ? storedPreferences.style
                : null,
            defaultState.style
        )
    ),
    view: parseGalleryView(
        initialParameters.get("view"),
        storedPreferences.view ?? defaultState.view
    ),
};

/** @param {BadgeCatalogEntry} entry */
function buildCard(entry) {
    const card = document.createElement("article");
    card.className = "layout-card";
    card.id = entry.id;
    card.dataset["primaryService"] = entry.providers[0] ?? "unknown";

    const header = document.createElement("header");
    header.className = "card-header";
    const headingGroup = document.createElement("div");
    const category = document.createElement("p");
    category.className = "category-tag";
    category.textContent = entry.category;
    const heading = document.createElement("h2");
    heading.textContent = entry.title;
    const languages = document.createElement("p");
    languages.className = "language-tags";
    languages.textContent = entry.languages.join(" · ");
    const services = document.createElement("div");
    services.className = "service-tags";
    for (const providerId of entry.providers) {
        const provider = badgeCatalog.providers.find(
            (candidate) => candidate.id === providerId
        );
        const tag = document.createElement("span");
        tag.dataset["service"] = providerId;
        tag.textContent = `${serviceIcon(providerId)} ${provider?.name ?? providerId}`;
        services.append(tag);
    }
    headingGroup.append(category, heading, languages, services);
    const source = document.createElement("a");
    source.className = "source-link";
    source.href = `${repositoryUrl}/blob/main/library.md#L${entry.sourceLine}`;
    source.textContent = "Source ↗";
    source.target = "_blank";
    source.rel = "noopener noreferrer";
    header.append(headingGroup, source);
    card.append(header);

    if (entry.description) {
        const description = document.createElement("p");
        description.className = "description";
        description.textContent = entry.description;
        card.append(description);
    }

    const previewHeader = document.createElement("div");
    previewHeader.className = "preview-header";
    const previewLabel = document.createElement("p");
    previewLabel.className = "preview-label";
    previewLabel.textContent =
        entry.placeholders.length > 0 ? "Personalized preview" : "Live preview";
    const stylePill = document.createElement("span");
    stylePill.className = "style-pill";
    stylePill.textContent = `${state.style === "flat" ? "◆" : "◇"} ${state.style}`;
    previewHeader.append(previewLabel, stylePill);
    const preview = document.createElement("div");
    preview.className = "badge-preview";
    preview.dataset["preview"] = "";
    card.append(previewHeader, preview);

    const editor = buildPlaceholderEditor(entry, card);
    if (editor) card.append(editor);

    const markdownWrap = document.createElement("div");
    markdownWrap.className = "markdown-wrap";
    const pre = document.createElement("pre");
    const code = document.createElement("code");
    code.dataset["markdown"] = "";
    pre.append(code);
    markdownWrap.append(pre);
    card.append(markdownWrap);

    const actions = document.createElement("div");
    actions.className = "card-actions";
    const actionButtons = document.createElement("div");
    actionButtons.className = "action-buttons";
    const copyButton = document.createElement("button");
    copyButton.className = "copy-button";
    copyButton.type = "button";
    copyButton.textContent = "⧉ Copy Markdown";
    copyButton.addEventListener("click", () => {
        void copyLayoutMarkdown(entry, copyButton);
    });
    const cliButton = document.createElement("button");
    cliButton.className = "secondary-button compact-button";
    cliButton.type = "button";
    cliButton.textContent = "⌘ Copy CLI";
    cliButton.addEventListener("click", () => {
        void copyLayoutCliCommand(entry);
    });
    actionButtons.append(copyButton, cliButton);
    const count = document.createElement("span");
    count.className = "badge-count";
    count.textContent = `${entry.badgeCount} badge${entry.badgeCount === 1 ? "" : "s"}`;
    actions.append(actionButtons, count);
    card.append(actions);

    updatePreview(entry, card);
    updateCode(entry, card);
    return card;
}

/** @param {BadgeCatalogEntry} entry */
function buildCliCommand(entry) {
    const localValues = valuesByEntry.get(entry.id) ?? {};
    const assignments = Object.entries(localValues)
        .filter(([, value]) => value.trim())
        .map(([name, value]) => {
            const assignment = `${name}=${value.trim()}`;
            return ` --set ${quoteCliArgument(assignment)}`;
        })
        .join("");
    const command = [
        "npx github-badge-layouts render",
        entry.id,
        "--owner",
        quoteCliArgument(state.owner),
        "--repo",
        quoteCliArgument(state.repo),
        "--branch",
        quoteCliArgument(state.branch),
        "--style",
        state.style,
    ].join(" ");
    return command + assignments;
}

/** @param {BadgeCatalogEntry} entry @param {HTMLElement} card */
function buildPlaceholderEditor(entry, card) {
    if (entry.placeholders.length === 0) return null;

    const details = document.createElement("details");
    details.className = "customizer";
    const summary = document.createElement("summary");
    summary.textContent = `Per-layout overrides (${entry.placeholders.length})`;
    details.append(summary);

    const fields = document.createElement("div");
    fields.className = "placeholder-grid";
    const values = valuesByEntry.get(entry.id) ?? {};
    const defaults = globalValues(entry);

    for (const placeholder of entry.placeholders) {
        const wrapper = document.createElement("div");
        wrapper.className = "placeholder-field";
        const inputId = `${entry.id}-${placeholder.replaceAll(/[^0-9a-z]+/giv, "-")}`;
        const label = document.createElement("label");
        label.className = "placeholder-label";
        label.htmlFor = inputId;
        label.textContent = placeholder;
        label.title = placeholder;
        const input = document.createElement("input");
        input.id = inputId;
        input.type = "text";
        input.value = values[placeholder] ?? "";
        input.placeholder =
            defaults[placeholder] ?? exampleValue(entry, placeholder);
        input.autocomplete = "off";
        input.spellcheck = false;
        input.addEventListener("input", () => {
            const nextValues = valuesByEntry.get(entry.id) ?? {};
            nextValues[placeholder] = input.value;
            valuesByEntry.set(entry.id, nextValues);
            updatePreview(entry, card);
            updateCode(entry, card);
        });
        wrapper.append(label, input);
        fields.append(wrapper);
    }

    const resetFields = document.createElement("button");
    resetFields.className = "reset-fields";
    resetFields.type = "button";
    resetFields.textContent = "↺ Clear overrides";
    resetFields.addEventListener("click", () => {
        valuesByEntry.delete(entry.id);
        fields.querySelectorAll("input").forEach((input) => {
            input.value = "";
        });
        updatePreview(entry, card);
        updateCode(entry, card);
        showToast(`Cleared overrides for ${entry.title}.`);
    });
    details.append(fields, resetFields);
    return details;
}

function contextIsValid() {
    const owner = ownerInput.value.trim();
    const repository = repositoryInput.value.trim();
    const branch = branchInput.value.trim();
    const isOwnerValid =
        owner.length > 0 &&
        owner.length <= 39 &&
        !owner.startsWith("-") &&
        !owner.endsWith("-") &&
        hasOnlyCharacters(
            owner,
            (character) => isAsciiAlphaNumeric(character) || character === "-"
        );
    const isRepositoryValid =
        repository.length > 0 &&
        repository.length <= 100 &&
        hasOnlyCharacters(
            repository,
            (character) =>
                isAsciiAlphaNumeric(character) || "._-".includes(character)
        );
    const isBranchValid = isValidGitRefName(branch);
    /**
     * @type {{
     *     input: HTMLInputElement;
     *     message: string;
     *     valid: boolean;
     * }[]}
     */
    const validity = [
        {
            input: ownerInput,
            message: "Enter a valid GitHub owner.",
            valid: isOwnerValid,
        },
        {
            input: repositoryInput,
            message: "Enter a valid repository name.",
            valid: isRepositoryValid,
        },
        {
            input: branchInput,
            message: "Enter a valid Git branch name.",
            valid: isBranchValid,
        },
    ];
    for (const { input, message, valid } of validity) {
        input.setAttribute("aria-invalid", String(!valid));
        input.setCustomValidity(valid ? "" : message);
    }
    return isOwnerValid && isRepositoryValid && isBranchValid;
}

/** @param {string} markdown @param {BadgeStyle} style */
function convertStyle(markdown, style) {
    const host =
        style === "flat" ? "https://flat.badgen.net/" : "https://badgen.net/";
    const replacement = `](${host}`;
    return markdown
        .replaceAll("](https://flat.badgen.net/", () => replacement)
        .replaceAll("](https://badgen.net/", () => replacement);
}

/** @param {BadgeCatalogEntry} entry */
async function copyLayoutCliCommand(entry) {
    try {
        await copyText(buildCliCommand(entry));
        showToast(`Copied CLI command for ${entry.title}.`);
    } catch (error) {
        showToast(error instanceof Error ? error.message : "Copy failed.");
    }
}

/** @param {BadgeCatalogEntry} entry @param {HTMLButtonElement} button */
async function copyLayoutMarkdown(entry, button) {
    try {
        await copyText(replacePlaceholders(entry, false));
        button.textContent = "✓ Copied";
        showToast(`Copied ${entry.title}.`);
        setTimeout(() => {
            button.textContent = "⧉ Copy Markdown";
        }, 1600);
    } catch (error) {
        showToast(
            error instanceof Error ? error.message : "Could not copy Markdown."
        );
    }
}

/** @param {string} text */
async function copyText(text) {
    if (!isSecureContext) {
        throw new Error("Clipboard access requires a secure browser context.");
    }
    await navigator.clipboard.writeText(text);
}

/** @param {BadgeCatalogEntry} entry @param {string} placeholder */
function exampleValue(entry, placeholder) {
    const haystack = `${entry.category} ${entry.languages.join(" ")} ${entry.title}`;
    const ecosystem = ecosystemExamples.find(({ test }) => test.test(haystack));
    return (
        ecosystem?.values[placeholder] ??
        generalExamples[placeholder] ??
        placeholder
    );
}

function getFilteredEntries() {
    return filterAndSortEntries(badgeCatalog.entries, {
        category: state.category,
        language: state.language,
        query: state.query,
        service: state.service,
        sort: state.sort,
    });
}

/** @param {BadgeCatalogEntry} entry @returns {Record<string, string>} */
function globalValues(entry) {
    return {
        BRANCH: state.branch,
        OWNER: state.owner,
        REPO: state.repo,
        ...Object.fromEntries(
            entry.placeholders
                .filter((name) => generalExamples[name] !== undefined)
                .map((name) => [name, exampleValue(entry, name)])
        ),
    };
}

/** @param {string} value @param {(character: string) => boolean} predicate */
function hasOnlyCharacters(value, predicate) {
    for (const character of value) {
        if (!predicate(character)) return false;
    }
    return true;
}

/** @param {string} character */
function isAsciiAlphaNumeric(character) {
    const codePoint = character.codePointAt(0) ?? -1;
    return (
        (codePoint >= 48 && codePoint <= 57) ||
        (codePoint >= 65 && codePoint <= 90) ||
        (codePoint >= 97 && codePoint <= 122)
    );
}

/** @param {string} value */
function isValidGitRefName(value) {
    if (
        value.length === 0 ||
        value.startsWith(".") ||
        value.startsWith("/") ||
        value.endsWith(".") ||
        value.endsWith("/") ||
        value.endsWith(".lock") ||
        value.includes("..") ||
        value.includes("//") ||
        value.includes("@{")
    ) {
        return false;
    }
    const invalidCharacters = " ~^:?*[]\\";
    for (const character of value) {
        const codePoint = character.codePointAt(0) ?? 0;
        const isControlCharacter =
            codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f);
        if (isControlCharacter || invalidCharacters.includes(character)) {
            return false;
        }
    }
    return true;
}

/** @param {string} value */
function normalizeReplacement(value) {
    let encoded = encodeURIComponent(value.trim());
    for (const character of [
        "!",
        "'",
        "(",
        ")",
        "*",
    ]) {
        const hexadecimal = (character.codePointAt(0) ?? 0)
            .toString(16)
            .toUpperCase();
        encoded = encoded.replaceAll(character, () => `%${hexadecimal}`);
    }
    return encoded;
}

/** @param {number} totalPages */
function paginationItems(totalPages) {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    const pages = new Set([
        1,
        state.page,
        state.page + 1,
        state.page - 1,
        totalPages,
    ]);
    const validPages = [...pages]
        .filter((page) => page >= 1 && page <= totalPages)
        .toSorted((left, right) => left - right);
    /** @type {(number | "ellipsis")[]} */
    const items = [];
    for (const page of validPages) {
        const previous = items.at(-1);
        if (typeof previous === "number" && page - previous > 1) {
            items.push("ellipsis");
        }
        items.push(page);
    }
    return items;
}

/** @param {string} markdown @returns {BadgeLink[]} */
function parseBadgeLinks(markdown) {
    /** @type {BadgeLink[]} */
    const links = [];
    const pattern =
        /\[!\[(?<alt>[^\]]*)\]\((?<image>https:\/\/[^\s\)]+)\)\]\((?<target>https:\/\/[^\s\)]+)\)/gv;
    let match;
    while ((match = pattern.exec(markdown)) !== null) {
        if (match.groups === undefined) continue;
        const { alt = "", image = "", target = "" } = match.groups;
        const imageUrl = securePreviewUrl(image, true);
        const targetUrl = securePreviewUrl(target, false);
        if (imageUrl !== null && targetUrl !== null) {
            links.push({ alt, image: imageUrl, target: targetUrl });
        }
    }
    return links;
}

function persistState() {
    try {
        localStorage.setItem(
            storageKey,
            JSON.stringify({
                pageSize: state.pageSize,
                sort: state.sort,
                style: state.style,
                view: state.view,
            })
        );
    } catch {
        // Private browsing modes can disable storage; the live state still works.
    }

    const parameters = new URLSearchParams();
    if (state.query) parameters.set("q", state.query);
    if (state.category !== defaultState.category) {
        parameters.set("category", state.category);
    }
    if (state.language !== defaultState.language) {
        parameters.set("language", state.language);
    }
    if (state.service !== defaultState.service) {
        parameters.set("service", state.service);
    }
    if (state.page !== defaultState.page)
        parameters.set("page", String(state.page));
    if (state.pageSize !== defaultState.pageSize) {
        parameters.set("perPage", String(state.pageSize));
    }
    if (state.owner !== defaultState.owner)
        parameters.set("owner", state.owner);
    if (state.repo !== defaultState.repo) parameters.set("repo", state.repo);
    if (state.branch !== defaultState.branch)
        parameters.set("branch", state.branch);
    if (state.sort !== defaultState.sort) parameters.set("sort", state.sort);
    if (state.style !== defaultState.style)
        parameters.set("style", state.style);
    if (state.view !== defaultState.view) parameters.set("view", state.view);
    const query = parameters.toString();
    const search = query.length > 0 ? `?${query}` : "";
    history.replaceState(null, "", location.pathname + search + location.hash);
}

/**
 * @param {BadgeCatalogEntry} entry
 * @param {string} placeholder
 * @param {string | undefined} localValue
 * @param {string | undefined} globalValue
 * @param {boolean} useExamples
 */
function placeholderReplacement(
    entry,
    placeholder,
    localValue,
    globalValue,
    useExamples
) {
    if (localValue !== undefined && localValue.length > 0) {
        return normalizeReplacement(localValue);
    }
    if (globalValue !== undefined && globalValue.length > 0) {
        return normalizeReplacement(globalValue);
    }
    if (useExamples) {
        return normalizeReplacement(exampleValue(entry, placeholder));
    }
    return placeholder;
}

/** @param {string} value */
function quoteCliArgument(value) {
    const escaped = value.replaceAll('"', () => String.raw`\"`);
    return `"${escaped}"`;
}

function render() {
    const entries = getFilteredEntries();
    const page = paginateEntries(entries, state.page, state.pageSize);
    state.page = page.page;

    grid.dataset["view"] = state.view;
    grid.ariaLabel = `Badge layouts in ${state.view} view`;
    grid.replaceChildren(
        ...page.visibleEntries.map((entry) => buildCard(entry))
    );
    emptyState.hidden = entries.length > 0;
    grid.hidden = entries.length === 0;
    resultCount.textContent = `Showing ${page.first}–${page.last} of ${entries.length} matching layouts · ${sortLabels[state.sort]} (${badgeCatalog.layoutCount} total)`;
    previewNoteText.textContent =
        state.view === "list"
            ? "Quick previews and copy actions; switch to grid to customize"
            : "Only this page loads badge images";
    contextSummary.textContent = `${state.owner}/${state.repo} · ${state.branch} · ${state.style} Badgen · ${badgeCatalog.providerCount} services`;
    renderPagination(page.totalPages);
    persistState();
}

/** @param {number} totalPages */
function renderPagination(totalPages) {
    pagination.hidden = totalPages <= 1;
    firstPageButton.disabled = state.page === 1;
    previousPageButton.disabled = state.page === 1;
    nextPageButton.disabled = state.page === totalPages;
    lastPageButton.disabled = state.page === totalPages;
    pageSummary.textContent = `Page ${state.page} of ${totalPages}`;
    pageList.replaceChildren();

    for (const item of paginationItems(totalPages)) {
        if (item === "ellipsis") {
            const separator = document.createElement("span");
            separator.className = "page-ellipsis";
            separator.textContent = "…";
            separator.ariaHidden = "true";
            pageList.append(separator);
            continue;
        }
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = String(item);
        button.ariaLabel = `Page ${item}`;
        if (item === state.page) {
            button.className = "current-page";
            button.ariaCurrent = "page";
        }
        button.addEventListener("click", () => {
            setPage(item);
        });
        pageList.append(button);
    }
}

/** @param {BadgeCatalogEntry} entry @param {boolean} useExamples */
function replacePlaceholders(entry, useExamples) {
    const localValues = valuesByEntry.get(entry.id) ?? {};
    const defaults = globalValues(entry);
    let markdown = entry.template;
    const sortedPlaceholders = entry.placeholders.toSorted(
        (left, right) => right.length - left.length
    );
    for (const placeholder of sortedPlaceholders) {
        const localValue = localValues[placeholder]?.trim();
        const globalValue = defaults[placeholder]?.trim();
        const replacement = placeholderReplacement(
            entry,
            placeholder,
            localValue,
            globalValue,
            useExamples
        );
        markdown = markdown.replaceAll(placeholder, () => replacement);
    }
    return convertStyle(markdown, state.style);
}

function resetContext() {
    state.owner = defaultState.owner;
    state.repo = defaultState.repo;
    state.branch = defaultState.branch;
    state.style = defaultState.style;
    ownerInput.value = state.owner;
    repositoryInput.value = state.repo;
    branchInput.value = state.branch;
    /** @type {HTMLInputElement} */
    const styleInput = queryRequired(
        document,
        `input[name="badge-style"][value="${state.style}"]`
    );
    styleInput.checked = true;
    contextIsValid();
    render();
    showToast("Restored Nick's repository defaults.");
}

function resetFilters() {
    state.query = defaultState.query;
    state.category = defaultState.category;
    state.language = defaultState.language;
    state.service = defaultState.service;
    state.page = defaultState.page;
    state.sort = defaultState.sort;
    searchInput.value = state.query;
    categorySelect.value = state.category;
    languageSelect.value = state.language;
    serviceSelect.value = state.service;
    sortSelect.value = state.sort;
    render();
    searchInput.focus();
}

function scheduleContextRender() {
    if (contextRenderState.timer !== null) {
        clearTimeout(contextRenderState.timer);
    }
    contextRenderState.timer = setTimeout(() => {
        if (!contextIsValid()) {
            contextSummary.textContent =
                "Fix the highlighted repository values to refresh layouts.";
            return;
        }
        state.owner = ownerInput.value.trim();
        state.repo = repositoryInput.value.trim();
        state.branch = branchInput.value.trim();
        render();
    }, 240);
}

/**
 * @param {string} value
 * @param {boolean} isBadgeImage
 *
 * @returns {string | null}
 */
function securePreviewUrl(value, isBadgeImage) {
    /** @type {URL} */
    let url;
    try {
        url = new URL(value);
    } catch {
        return null;
    }
    if (
        url.protocol !== "https:" ||
        url.username.length > 0 ||
        url.password.length > 0
    ) {
        return null;
    }
    if (isBadgeImage && !allowedPreviewImageHosts.has(url.hostname)) {
        return null;
    }
    return url.href;
}

/** @param {string} providerId */
function serviceIcon(providerId) {
    return (
        {
            "badge-fury": "⬢",
            "badge-size": "↔",
            badgen: "◆",
            codecov: "◔",
            "dependents-info": "⑂",
            nodeico: "⬡",
            playbadges: "▶",
            shieldcn: "◇",
            shields: "⬟",
            snyk: "⚿",
        }[providerId] ?? "●"
    );
}

/** @param {number} page @param {boolean} [shouldScroll] */
function setPage(page, shouldScroll = true) {
    if (page === state.page) return;
    state.page = page;
    render();
    if (shouldScroll) {
        resultCount.scrollIntoView({ behavior: "smooth", block: "start" });
    }
}

/** @param {string} message */
function showToast(message) {
    if (toastState.timer !== null) clearTimeout(toastState.timer);
    toast.textContent = message;
    toast.classList.add("visible");
    toastState.timer = setTimeout(() => {
        toast.classList.remove("visible");
    }, 2200);
}

/** @param {BadgeCatalogEntry} entry @param {HTMLElement} card */
function updateCode(entry, card) {
    queryRequired(card, "[data-markdown]").textContent = replacePlaceholders(
        entry,
        false
    );
}

/** @param {BadgeCatalogEntry} entry @param {HTMLElement} card */
function updatePreview(entry, card) {
    /** @type {HTMLElement} */
    const preview = queryRequired(card, "[data-preview]");
    preview.replaceChildren();
    const badges = parseBadgeLinks(replacePlaceholders(entry, true));
    for (const badge of badges) {
        const link = document.createElement("a");
        link.href = badge.target;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.title = `${badge.alt} Open destination in a new tab.`;

        const image = document.createElement("img");
        image.src = badge.image;
        image.alt = badge.alt;
        image.height = 20;
        image.loading = "lazy";
        image.decoding = "async";
        image.fetchPriority = "low";
        image.referrerPolicy = "no-referrer";
        image.addEventListener(
            "error",
            () => {
                const error = document.createElement("span");
                error.className = "badge-error";
                error.textContent = badge.alt;
                link.replaceChildren(error);
            },
            { once: true }
        );
        link.append(image);
        preview.append(link);
    }
}

for (const category of badgeCatalog.categories) {
    const option = document.createElement("option");
    option.value = category;
    option.textContent = category;
    categorySelect.append(option);
}

for (const language of badgeCatalog.languages) {
    const option = document.createElement("option");
    option.value = language;
    option.textContent = language;
    languageSelect.append(option);
}

for (const provider of badgeCatalog.providers) {
    const option = document.createElement("option");
    option.value = provider.id;
    option.textContent = `${provider.name} (${provider.layoutCount})`;
    serviceSelect.append(option);
}

ownerInput.value = state.owner;
repositoryInput.value = state.repo;
branchInput.value = state.branch;
searchInput.value = state.query;
categorySelect.value = badgeCatalog.categories.includes(state.category)
    ? state.category
    : defaultState.category;
state.category = categorySelect.value;
languageSelect.value = badgeCatalog.languages.includes(state.language)
    ? state.language
    : defaultState.language;
state.language = languageSelect.value;
serviceSelect.value = badgeCatalog.providers.some(
    (provider) => provider.id === state.service
)
    ? state.service
    : defaultState.service;
state.service = serviceSelect.value;
sortSelect.value = state.sort;
const allowedPageSizes = [
    4,
    6,
    9,
    12,
];
pageSizeSelect.value = String(defaultState.pageSize);
if (allowedPageSizes.includes(state.pageSize)) {
    pageSizeSelect.value = String(state.pageSize);
}
state.pageSize = Number(pageSizeSelect.value);
/** @type {HTMLInputElement} */
const initialStyleInput = queryRequired(
    document,
    `input[name="badge-style"][value="${state.style}"]`
);
initialStyleInput.checked = true;
/** @type {HTMLInputElement} */
const initialViewInput = queryRequired(
    document,
    `input[name="gallery-view"][value="${state.view}"]`
);
initialViewInput.checked = true;

queryRequired(document, "#layout-total").textContent =
    badgeCatalog.layoutCount.toLocaleString();
queryRequired(document, "#badge-total").textContent =
    badgeCatalog.badgeCount.toLocaleString();
queryRequired(document, "#category-total").textContent =
    badgeCatalog.categoryCount.toLocaleString();
queryRequired(document, "#language-total").textContent =
    badgeCatalog.languageCount.toLocaleString();
queryRequired(document, "#service-total").textContent =
    badgeCatalog.providerCount.toLocaleString();

searchInput.addEventListener("input", () => {
    state.query = searchInput.value;
    state.page = 1;
    render();
});
categorySelect.addEventListener("change", () => {
    state.category = categorySelect.value;
    state.page = 1;
    render();
});
languageSelect.addEventListener("change", () => {
    state.language = languageSelect.value;
    state.page = 1;
    render();
});
serviceSelect.addEventListener("change", () => {
    state.service = serviceSelect.value;
    state.page = 1;
    render();
});
sortSelect.addEventListener("change", () => {
    state.sort = parseSortMode(sortSelect.value, defaultState.sort);
    state.page = 1;
    render();
});
pageSizeSelect.addEventListener("change", () => {
    state.pageSize = Number(pageSizeSelect.value);
    state.page = 1;
    render();
});
for (const input of [
    ownerInput,
    repositoryInput,
    branchInput,
]) {
    input.addEventListener("input", scheduleContextRender);
    input.addEventListener("blur", () => {
        input.reportValidity();
    });
}
document.querySelectorAll('input[name="badge-style"]').forEach((input) => {
    input.addEventListener("change", () => {
        if (!(input instanceof HTMLInputElement) || !input.checked) return;
        state.style = /** @type {BadgeStyle} */ (input.value);
        render();
    });
});
document.querySelectorAll('input[name="gallery-view"]').forEach((input) => {
    input.addEventListener("change", () => {
        if (!(input instanceof HTMLInputElement) || !input.checked) return;
        state.view = parseGalleryView(input.value, defaultState.view);
        render();
    });
});
resetFiltersButton.addEventListener("click", resetFilters);
resetContextButton.addEventListener("click", resetContext);
queryRequired(document, "[data-reset-filters]").addEventListener(
    "click",
    resetFilters
);
firstPageButton.addEventListener("click", () => {
    setPage(1);
});
previousPageButton.addEventListener("click", () => {
    setPage(state.page - 1);
});
nextPageButton.addEventListener("click", () => {
    setPage(state.page + 1);
});
lastPageButton.addEventListener("click", () => {
    setPage(Math.ceil(getFilteredEntries().length / state.pageSize));
});
document.addEventListener("keydown", (event) => {
    const activeTag = document.activeElement?.tagName ?? "";
    const isTyping = [
        "INPUT",
        "SELECT",
        "TEXTAREA",
    ].includes(activeTag);
    if (!isTyping && event.key === "/") {
        event.preventDefault();
        searchInput.focus();
    }
    if (!isTyping && state.page > 1 && event.key === "[") {
        setPage(state.page - 1);
    }
    if (!isTyping && event.key === "]") {
        const totalPages = Math.ceil(
            getFilteredEntries().length / state.pageSize
        );
        if (state.page < totalPages) setPage(state.page + 1);
    }
});

contextIsValid();
const initialTargetId = decodeHashTarget(location.hash);
if (initialTargetId) {
    const targetIndex = getFilteredEntries().findIndex(
        (entry) => entry.id === initialTargetId
    );
    if (targetIndex !== -1) {
        state.page = Math.floor(targetIndex / state.pageSize) + 1;
    }
}
render();
if (initialTargetId) {
    requestAnimationFrame(() =>
        document
            .querySelector(`#${CSS.escape(initialTargetId)}`)
            ?.scrollIntoView()
    );
}
